const FONT_FAMILY = "Verdana";
const HORIZONTAL_PADDING = 7;
const VERTICAL_PADDING = 4;
const LINE_HEIGHT = 1.2;

let measurementContext: CanvasRenderingContext2D | null = null;

type NameplateLayout = {
    text: string;
    width: number;
    height: number;
    top: number;
    fontSize: number;
    paddingX: number;
    paddingY: number;
    lineHeight: number;
    fontFamily: string;
};

const layoutCache = new Map<string, NameplateLayout>();

function measureName(text: string, fontSize: number): number {
    if (!measurementContext && typeof document !== "undefined") {
        measurementContext = document.createElement("canvas").getContext("2d");
    }
    if (!measurementContext) return text.length * fontSize * 0.7;
    measurementContext.font = `bold ${fontSize}px ${FONT_FAMILY}`;
    return measurementContext.measureText(text).width;
}

export function tokenNameplateLayout(
    name: string,
    tokenSize: number,
    measure: (text: string, fontSize: number) => number = measureName,
): NameplateLayout {
    const cacheKey = `${tokenSize}:${name}`;
    if (measure === measureName) {
        const cached = layoutCache.get(cacheKey);
        if (cached) return cached;
    }
    const fontSize = Math.min(16, Math.max(10, tokenSize * 0.145));
    const frameRadius = tokenSize / 2;
    const words = name.trim().split(/\s+/).filter(Boolean);
    const fullName = words.join(" ");
    const availableWidth = tokenSize - 2 * HORIZONTAL_PADDING;
    let lines = [fullName];

    if (words.length > 1 && measure(fullName, fontSize) > availableWidth) {
        let smallestWidth = Infinity;
        for (let split = 1; split < words.length; split++) {
            const candidate = [words.slice(0, split).join(" "), words.slice(split).join(" ")];
            const width = Math.max(...candidate.map(line => measure(line, fontSize)));
            if (width < smallestWidth) {
                smallestWidth = width;
                lines = candidate;
            }
        }
    }

    const textWidth = Math.max(...lines.map(line => measure(line, fontSize)));
    const width = Math.max(frameRadius * 1.2, textWidth + 2 * HORIZONTAL_PADDING + 2);
    const height = lines.length * fontSize * LINE_HEIGHT + 2 * VERTICAL_PADDING;
    // Align the bottom of the first text line with radius 340 of the 800 px frame.
    const top = frameRadius * (340 / 400) - VERTICAL_PADDING - fontSize * LINE_HEIGHT;
    const layout = {
        text: lines.join("\n"), width, height, top, fontSize,
        paddingX: HORIZONTAL_PADDING, paddingY: VERTICAL_PADDING,
        lineHeight: LINE_HEIGHT, fontFamily: FONT_FAMILY,
    };
    if (measure === measureName) {
        if (layoutCache.size >= 256) layoutCache.delete(layoutCache.keys().next().value!);
        layoutCache.set(cacheKey, layout);
    }
    return layout;
}
